/* eslint-disable no-unused-vars -- parameter names document the vendor queue contract */
import { browser } from '$app/environment';
import type { PixelLoadState } from './tiktok-pixel';

type RedditQueue = ((...args: unknown[]) => void) & {
  queue: unknown[][];
  callQueue: unknown[][];
  sendEvent?: (...args: unknown[]) => void;
};

export interface RedditPixel {
  init(): Promise<boolean>;
  pageVisit(): boolean;
  track(event: string, metadata?: Record<string, unknown>): boolean;
  getState(): PixelLoadState;
}

const SCRIPT_ID = 'quilty-reddit-pixel';
const SCRIPT_URL = 'https://www.redditstatic.com/ads/pixel.js';

export function createRedditPixel(pixelId: string): RedditPixel {
  let state: PixelLoadState = pixelId.trim() ? 'idle' : 'disabled';
  let initPromise: Promise<boolean> | null = null;
  let initQueued = false;

  function init(): Promise<boolean> {
    if (!browser || state === 'disabled') return Promise.resolve(false);
    if (state === 'ready') return Promise.resolve(true);
    if (initPromise) return initPromise;

    if (!initQueued) {
      getOrCreateQueue()('init', pixelId, {
        optOut: false,
        useDecimalCurrencyValues: true,
      });
      initQueued = true;
    }
    state = 'loading';
    initPromise = loadScript().then((loaded) => {
      state = loaded ? 'ready' : 'error';
      if (!loaded) initPromise = null;
      return loaded;
    });
    return initPromise;
  }

  return {
    init,
    pageVisit: () => callReddit('track', 'PageVisit'),
    track: (event, metadata = {}) => callReddit('track', event, metadata),
    getState: () => state,
  };
}

function getOrCreateQueue(): RedditQueue {
  const existing = (window as unknown as { rdt?: RedditQueue }).rdt;
  if (existing) return existing;
  const queue = ((...args: unknown[]) => {
    if (queue.sendEvent) {
      queue.sendEvent(...args);
    } else {
      queue.callQueue.push(args);
    }
  }) as RedditQueue;
  queue.queue = [];
  queue.callQueue = queue.queue;
  (window as unknown as { rdt: RedditQueue }).rdt = queue;
  return queue;
}

function callReddit(...args: unknown[]): boolean {
  if (!browser) return false;
  const rdt = (window as unknown as { rdt?: RedditQueue }).rdt;
  if (typeof rdt !== 'function') return false;
  try {
    rdt(...args);
    return true;
  } catch {
    return false;
  }
}

function loadScript(): Promise<boolean> {
  return new Promise((resolve) => {
    let existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing?.dataset.quiltyPixelState === 'ready' || isRedditLoaded()) {
      if (existing) existing.dataset.quiltyPixelState = 'ready';
      resolve(true);
      return;
    }
    if (existing?.dataset.quiltyPixelState === 'error') {
      if (existing.dataset.quiltyPixelOwned === 'true') existing.remove();
      else {
        resolve(false);
        return;
      }
      existing = null;
    }
    const element = existing ?? document.createElement('script');
    element.addEventListener('load', () => {
      element.dataset.quiltyPixelState = 'ready';
      resolve(true);
    }, { once: true });
    element.addEventListener('error', () => {
      element.dataset.quiltyPixelState = 'error';
      resolve(false);
    }, { once: true });
    if (!existing) {
      element.id = SCRIPT_ID;
      element.async = true;
      element.src = SCRIPT_URL;
      element.dataset.quiltyPixelOwned = 'true';
      element.dataset.quiltyPixelState = 'loading';
      document.head.appendChild(element);
    }
  });
}

function isRedditLoaded(): boolean {
  const rdt = (window as unknown as { rdt?: RedditQueue }).rdt;
  return typeof rdt?.sendEvent === 'function';
}
